import { supabase } from './supabase';

// ---------------------------------------------------------------------------
// Error message normalizer
// Maps known Gemini/API error signatures to friendly user-facing messages.
// ---------------------------------------------------------------------------

export function getAiErrorMessage(error: unknown): string {
    const raw = error instanceof Error ? error.message : String(error);
    const lower = raw.toLowerCase();

    if (lower.includes('consumer_suspended') || lower.includes('has been suspended')) {
        return 'Nossa IA está temporariamente indisponível. Estamos trabalhando para resolver o mais rápido possível.';
    }
    if (lower.includes('quota') || lower.includes('resource_exhausted') || lower.includes('429')) {
        return 'Nossa IA está sobrecarregada no momento. Aguarde alguns instantes e tente novamente.';
    }
    if (lower.includes('permission_denied') || lower.includes('403')) {
        return 'Nossa IA está temporariamente indisponível. Estamos trabalhando para resolver o mais rápido possível.';
    }
    if (lower.includes('não autenticado') || lower.includes('401')) {
        return 'Sua sessão expirou. Por favor, faça login novamente.';
    }
    if (lower.includes('formato inválido')) {
        return 'A IA retornou um formato inesperado. Tente reformular sua descrição.';
    }
    if (lower.includes('network') || lower.includes('fetch') || lower.includes('failed to fetch')) {
        return 'Erro de conexão. Verifique sua internet e tente novamente.';
    }

    // Fallback genérico — nunca exibe o erro técnico bruto
    return 'Algo deu errado ao processar sua solicitação. Tente novamente.';
}



// Utility wrapper for retries with exponential backoff (skips retries on non-retryable 4xx errors)
const invokeWithRetry = async (functionName: string, body: Record<string, unknown>, headers: Record<string, string>, retries = 3, backoff = 1000) => {
    for (let i = 0; i < retries; i++) {
        const { data, error } = await supabase.functions.invoke(functionName, { body, headers });
        
        // If success
        if (!error && data && !data.error) {
            return data;
        }

        // Extract detailed error if present
        let detailedError = error?.message || data?.error || '';
        let isNonRetryable = false;

        if (error) {
            try {
                const ctx = (error as { context?: { status?: number; json?: () => Promise<{ error?: string; message?: string; code?: string }>, error?: string } }).context;
                if (ctx) {
                    if (ctx.status && ctx.status >= 400 && ctx.status < 500 && ctx.status !== 429) {
                        isNonRetryable = true;
                    }
                    if (typeof ctx.json === 'function') {
                        const json = await ctx.json();
                        detailedError = json.message || json.error || JSON.stringify(json);
                    } else if (ctx.error) {
                        detailedError = ctx.error;
                    }
                }
            } catch (e) {
                console.error("Failed to parse detailed error context", e);
            }
        }

        // Non-retryable errors (e.g. 403 upgrade_required, plan_overflow, invalid payload) fail fast immediately
        if (isNonRetryable || detailedError.includes('subscription_required') || detailedError.includes('plan_overflow') || detailedError.includes('upgrade_required')) {
            throw new Error(detailedError || 'Operação não permitida pelo seu plano atual.');
        }

        // Determine if error is retryable (429 or 5xx or network failures)
        console.warn(`Retry ${i + 1}/${retries} failed for ${functionName}`, detailedError || error);
        if (i < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, backoff * Math.pow(2, i))); // Exponential backoff
        } else {
            throw new Error(detailedError || `Invocação falhou após ${retries} tentativas.`);
        }
    }
};

export const callGemini = async (prompt: string, systemInstruction = "") => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Usuário não autenticado.");

    const data = await invokeWithRetry('gemini-persona', { prompt, systemInstruction }, {
        Authorization: `Bearer ${session.access_token}`
    });

    return data?.text || "";
};

export const callGeminiChat = async (conversationId: string, messageContent: string): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Usuário não autenticado.");

    const data = await invokeWithRetry('gemini-chat', { conversationId, messageContent }, {
        Authorization: `Bearer ${session.access_token}`
    });

    return data?.text || "";
};




export const callGeminiImage = async (imagePrompt: string, personaId?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Usuário não autenticado.");

    const data = await invokeWithRetry('gemini-image', { prompt: imagePrompt, personaId }, {
        Authorization: `Bearer ${session.access_token}`
    });

    return data?.imageUrl || "";
};
